/**
 * WebEngage Analytics Adapter
 * Infrastructure layer - Implements EventTracker using WebEngage SDK
 * Extends BaseAdapter to transform domain events to WebEngage DTOs
 */

import { BaseAdapter } from './base.adapter';
import { warnOnce } from './logger';
import type { WebEngageEventDTO } from './webenage.dto';
import type { Event } from '../../domain/models/Event';
import { getWebEngageCommonAttributes } from '../../utils/commonAttributes';

/**
 * WebEngage SDK global type
 */
interface WebEngageSDK {
  track: (eventName: string, attributes?: Record<string, unknown>) => void;
  init?: (licenseCode: string) => void;
  onReady?: (callback: () => void) => void;
  __queue?: Array<[string, unknown[]]>;
  state?: {
    getForever: () => { luid: string };
  };
  user: {
    login: (userId: string) => void;
    identify: (userId: string, attributes?: Record<string, unknown>) => void;
    setAttribute: (attributeName: string, attributeValue: unknown) => void;
    setAttributes: (attributes: Record<string, unknown>) => void;
  };
}

declare global {
  interface Window {
    webengage?: WebEngageSDK;
    we_id?: string;
    onload?: (() => void) | null;
  }
}

/**
 * WebEngage Configuration
 */
export interface WebenageConfig {
  licenseCode: string;
  enabled?: boolean;
}

/**
 * WebEngage Analytics Adapter
 * Extends BaseAdapter and implements WebEngage-specific transformations
 */
export class WebenageAdapter extends BaseAdapter {
  private config: WebenageConfig;

  constructor(config: WebenageConfig) {
    super();
    this.config = {
      enabled: true,
      ...config,
    };
  }

  async initialize(): Promise<void> {
    if (this.initialized || !this.config.enabled) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    // Check if script tag exists (for debugging)
    const scriptTag = document.getElementById('_webengage_script_tag');
    if (!scriptTag) {
      warnOnce('we:script-tag-not-found', 'WebEngage script tag not found in DOM - script may not be injected');
    }

    // Wait for WebEngage SDK to be loaded (injected server-side)
    await this.waitForWebEngage();
    this.initialized = true;
  }

  /**
   * Transform domain event to WebEngage DTO
   */
  protected transformEventToDTO(event: Event): WebEngageEventDTO {
    // Merge properties and context into attributes
    // WebEngage accepts flat key-value pairs
    const attributes: Record<string, unknown> = {
      ...event.properties,
      ...getWebEngageCommonAttributes(),
      // Include context as attributes with prefixes
      ...(event.context.page && { page: event.context.page }),
      ...(event.context.section && { section: event.context.section }),
      ...(event.context.component && { component: event.context.component }),
      ...(event.context.userId && { userId: event.context.userId }),
      ...(event.context.sessionId && { sessionId: event.context.sessionId }),
    };

    // Extract WebEngage-specific metadata from context
    const metadata: WebEngageEventDTO['metadata'] = {
      sessionId: event.context.sessionId,
      userId: event.context.userId,
      page: event.context.page,
      section: event.context.section,
      component: event.context.component,
    };

    return {
      eventName: event.name,
      attributes,
      metadata,
    };
  }

  /**
   * Track event using WebEngage DTO
   */
  protected trackEventDTO(dto: unknown): void {
    if (!this.config.enabled) {
      warnOnce('we:disabled', 'WebEngage adapter is disabled');
      return;
    }

    if (!this.initialized) {
      warnOnce('we:not-initialized', 'WebEngage adapter not initialized yet, event will not be tracked');
      return;
    }

    if (typeof window === 'undefined') {
      warnOnce('we:window-undefined', 'Window is undefined, cannot track event');
      return;
    }

    if (!window.webengage) {
      warnOnce('we:sdk-unavailable', 'WebEngage SDK not available on window object');
      return;
    }

    const webengageDTO = dto as WebEngageEventDTO;
    
    // WebEngage SDK accepts event name and flat attributes
    // We merge attributes and metadata for tracking
    const allAttributes: Record<string, unknown> = {
      ...webengageDTO.attributes,
      ...(webengageDTO.metadata && Object.entries(webengageDTO.metadata).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, unknown>)),
    };

    try {
      window.webengage.track(webengageDTO.eventName, allAttributes);
    } catch (error) {
      console.error('Error tracking event to WebEngage:', error);
    }
  }

  /**
   * Identify a logged-in user and set their profile attributes in WebEngage.
   * Called after successful login or signup.
   */
  loginUser(
    userCode: string,
    attributes?: { email?: string; firstName?: string; phone?: string }
  ): void {
    if (!this.config.enabled || !this.initialized || typeof window === 'undefined' || !window.webengage) {
      return;
    }

    try {
      if (userCode) {
        window.webengage.user.login(userCode);
      }
      if (attributes?.email) {
        window.webengage.user.setAttribute('we_email', attributes.email);
      }
      if (attributes?.firstName) {
        window.webengage.user.setAttribute('we_first_name', attributes.firstName);
      }
      if (attributes?.phone) {
        window.webengage.user.setAttribute('we_phone', attributes.phone);
      }
    } catch (error) {
      console.error('Error setting WebEngage user attributes:', error);
    }
  }

  /**
   * Wait for WebEngage SDK to be loaded (injected server-side)
   */
  private async waitForWebEngage(): Promise<void> {
    return new Promise((resolve) => {
      if (typeof window === 'undefined') {
        resolve();
        return;
      }

      // Helper to check if SDK is ready
      const isSDKReady = (): boolean => {
        return !!(window.webengage && typeof window.webengage.init === 'function');
      };

      // Helper to check if SDK is in queue state (script loaded but not initialized)
      const isSDKInQueue = (): boolean => {
        return !!(window.webengage && window.webengage.__queue);
      };

      // Check if already loaded and initialized
      if (isSDKReady()) {
        resolve();
        return;
      }

      // If webengage object exists (even in queue state), we know script is loading
      if (isSDKInQueue()) {
        // Wait for the actual SDK to load and initialize
        let attempts = 0;
        const maxAttempts = 100; // 10 seconds total (100 * 100ms)
        
        const checkInterval = setInterval(() => {
          attempts++;
          
          if (isSDKReady()) {
            clearInterval(checkInterval);
            resolve();
            return;
          }
          
          // Timeout after max attempts
          if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            warnOnce('we:init-timeout-queue', 'WebEngage SDK initialization timeout - events may be queued');
            resolve(); // Resolve anyway to not block analytics
            return;
          }
        }, 100);
      } else {
        // Wait for script to create webengage object (might take a moment for script to execute)
        let attempts = 0;
        const maxAttempts = 150; // 15 seconds total (150 * 100ms) - longer timeout for script execution
        
        const checkInterval = setInterval(() => {
          attempts++;
          
          // Check if webengage object exists (even in queue state)
          if (window.webengage) {
            // If it's ready, resolve immediately
            if (isSDKReady()) {
              clearInterval(checkInterval);
              resolve();
              return;
            }
            
            // If it's in queue state, switch to waiting for full initialization
            if (isSDKInQueue()) {
              clearInterval(checkInterval);
              
              // Now wait for full initialization
              let initAttempts = 0;
              const maxInitAttempts = 100; // 10 more seconds
              
              const initInterval = setInterval(() => {
                initAttempts++;
                
                if (isSDKReady()) {
                  clearInterval(initInterval);
                  resolve();
                  return;
                }
                
                if (initAttempts >= maxInitAttempts) {
                  clearInterval(initInterval);
                  warnOnce('we:init-timeout-queue', 'WebEngage SDK initialization timeout - events may be queued');
                  resolve();
                  return;
                }
              }, 100);
              
              return;
            }
          }
          
          // Timeout after max attempts
          if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            warnOnce('we:sdk-timeout', 'WebEngage SDK not found after timeout - check script injection and environment variables. Make sure VITE_WEBENAGE_LICENSE_CODE and VITE_WEBENAGE_ENABLED are set correctly.');
            resolve(); // Resolve anyway to not block analytics
            return;
          }
        }, 100);
      }
    });
  }
}

